import { useState, useEffect, useRef } from 'react';
import { lumi } from '@/lib/lumi';
import { ensureProfile } from '@/lib/profileStore';
import { Link } from 'react-router-dom';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import {Mail, Lock, User, ChefHat, UtensilsCrossed, Eye, EyeOff, ArrowRight, ArrowLeft, CheckCircle2} from 'lucide-react';

type Mode = 'signup' | 'signin' | 'reset';
type ProfileType = 'trabalhador' | 'restaurante' | null;

function isValidEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}

function mapAuthError(e: any, context: 'signup' | 'signin' | 'reset'): string {
  const raw = String(e?.message || e?.error || '').toLowerCase();
  const code = String(e?.code || '').toLowerCase();

  if (typeof navigator !== 'undefined' && !navigator.onLine) {
    return 'Falha de conexao. Verifique sua internet e tente novamente.';
  }
  if (raw.includes('network') || raw.includes('fetch') || raw.includes('timeout')) {
    return 'Falha de conexao. Verifique sua internet e tente novamente.';
  }
  if (raw.includes('already') || raw.includes('exist') || code.includes('exist') || code.includes('duplicate')) {
    return 'Este e-mail ja esta cadastrado. Tente entrar ou recuperar sua senha.';
  }
  if (raw.includes('invalid email') || raw.includes('email invalido')) {
    return 'E-mail invalido. Verifique e tente novamente.';
  }
  if ((raw.includes('password') || raw.includes('senha')) && (raw.includes('short') || raw.includes('weak') || raw.includes('curta') || raw.includes('fraca'))) {
    return 'Senha muito curta. Use pelo menos 6 caracteres.';
  }
  if (raw.includes('otp') || raw.includes('codigo') || raw.includes('verification') || raw.includes('verificacao')) {
    return 'Codigo de verificacao invalido ou expirado.';
  }
  if (context === 'signin' && (raw.includes('invalid') || raw.includes('incorrect') || raw.includes('wrong') || raw.includes('credential') || raw.includes('unauthorized') || raw.includes('not found'))) {
    return 'E-mail ou senha incorretos.';
  }
  return e?.message || 'Ocorreu um erro. Tente novamente.';
}

function Cadastro() {
  const [mode, setMode] = useState<Mode>('signup');
  const [profileType, setProfileType] = useState<ProfileType>(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [userName, setUserName] = useState('');
  const [otp, setOtp] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [sendingOtp, setSendingOtp] = useState(false);
  const authBusy = useRef(false);
  const otpBusy = useRef(false);
  const [needsCompletion, setNeedsCompletion] = useState(false);
  const [notice, setNotice] = useState('');
  const [countdown, setCountdown] = useState(0);
  const [otpSent, setOtpSent] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [step, setStep] = useState<'select-type' | 'form'>('select-type');
  const [referredBy, setReferredBy] = useState<string>('');

  // Captura o codigo de indicacao (?ref=CODIGO) presente na URL de convite
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const ref = params.get('ref');
      if (ref && /^[A-Za-z0-9]{4,40}$/.test(ref.trim())) setReferredBy(ref.trim().toUpperCase());
    } catch (e) {
      console.error('Erro ao ler codigo de indicacao:', e);
    }
  }, []);

  useEffect(() => {
    if (countdown > 0) {
      const t = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(t);
    }
  }, [countdown]);

  const sendOtp = async () => {
    if (otpBusy.current || countdown > 0 || loading) return;
    if (!isValidEmail(email)) return setError('Insira um e-mail válido.');
    otpBusy.current = true; setSendingOtp(true); setError('');
    try {
      await lumi.auth.sendOtp({ email: email.trim(), type: mode === 'reset' ? 'resetPassword' : 'register' });
      setCountdown(60); setOtpSent(true);
    } catch (e: any) { setError(e?.message || 'Erro ao enviar o código. Tente novamente.'); }
    finally { otpBusy.current = false; setSendingOtp(false); }
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (authBusy.current || sendingOtp || needsCompletion) return;
    setError('');
    if (mode === 'signup' && !profileType) return setError('Selecione o tipo de perfil.');

    if (!isValidEmail(email)) {
      return setError('E-mail invalido. Verifique e tente novamente.');
    }
    if (mode === 'signup') {
      if (password.length < 6) {
        return setError('Senha muito curta. Use pelo menos 6 caracteres.');
      }
      if (password !== confirmPassword) {
        return setError('As senhas nao coincidem.');
      }
      if (!termsAccepted) {
        return setError('Voce precisa aceitar os Termos de Uso e a Politica de Privacidade para continuar.');
      }
      if (!otp) {
        return setError('Por favor, insira o codigo de verificacao.');
      }
    }
    if (mode === 'reset') {
      if (password.length < 6) {
        return setError('Senha muito curta. Use pelo menos 6 caracteres.');
      }
      if (!otp) {
        return setError('Por favor, insira o codigo de verificacao.');
      }
    }

    authBusy.current = true;
    setLoading(true);
    try {
      const redirectTo = '/perfil';

      if (mode === 'signin') {
        await lumi.auth.signInWithPassword({ email: email.trim(), password });
        window.location.href = redirectTo;
      } else if (mode === 'signup') {
        const session = await lumi.auth.signUpWithPassword({ email: email.trim(), password, otp: otp.trim(), userName: userName.trim() || undefined });
        const createdUser = session?.user || lumi.auth.user;
        if (!createdUser?.userId || !profileType) throw new Error('Entre na sua conta para concluir o cadastro.');
        try {
          await ensureProfile(lumi.entities.user_profiles, createdUser, profileType, referredBy);
          window.location.href = redirectTo;
        } catch {
          setNeedsCompletion(true);
          setError('Sua conta foi criada, mas o perfil não pôde ser salvo. Use “Concluir cadastro” para tentar novamente.');
        }
      } else if (mode === 'reset') {
        await lumi.auth.resetPassword({ email: email.trim(), newPassword: password, otp: otp.trim() });
        switchMode('signin');
        setNotice('Senha atualizada. Entre com sua nova senha.');
      }
    } catch (e: any) {
      setError(mapAuthError(e, mode));
    }
    setLoading(false);
    authBusy.current = false;
  };

  const handleGoogleLogin = async () => {
    if (authBusy.current || needsCompletion) return;
    if (mode === 'signup' && (!profileType || !termsAccepted)) {
      setError('Selecione o tipo de perfil e aceite os termos antes de continuar.'); return;
    }
    authBusy.current = true; setLoading(true); setError('');
    try {
      const session = await lumi.auth.signInWithOAuth({ provider: 'google' });
      const signedUser = session?.user || lumi.auth.user;
      if (!signedUser?.userId) throw new Error('Não foi possível confirmar o login. Tente novamente.');
      if (profileType) {
        try { await ensureProfile(lumi.entities.user_profiles, signedUser, profileType, referredBy); }
        catch { setNeedsCompletion(true); setError('Você entrou, mas seu perfil precisa ser concluído.'); return; }
      }
      window.location.href = '/perfil';
    } catch (e: any) { setError(e?.message || 'Não foi possível entrar com Google.'); }
    finally { setLoading(false); authBusy.current = false; }
  };

  const switchMode = (newMode: Mode) => {
    setMode(newMode);
    setNotice('');
    setCountdown(0);
    setError('');
    setOtp('');
    setOtpSent(false);
    setPassword('');
    setConfirmPassword('');
    setTermsAccepted(false);
  };

  const resetToTypeSelection = () => {
    setStep('select-type');
    setProfileType(null);
    setError('');
    setOtp('');
    setOtpSent(false);
    setEmail('');
    setPassword('');
    setConfirmPassword('');
    setTermsAccepted(false);
    setUserName('');
    switchMode('signup');
  };

  return (
    <div className="min-h-screen bg-[#1a1a1a] flex flex-col">
      <Navbar />

      <div className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-lg">

          {notice && <p role="status" className="text-green-300 mb-4">{notice}</p>}
          {needsCompletion && <div className="mb-6 rounded-xl bg-[#333] p-4 text-white"><p>Sua conta já existe. Você pode concluir o perfil sem repetir o cadastro.</p><Link to="/completar-cadastro" className="inline-block mt-3 underline">Concluir cadastro</Link></div>}
          {/* Etapa 1: Selecao do tipo de perfil */}
          {step === 'select-type' &&
          <div className="animate-fade-in">
              <div className="text-center mb-10">
                <h1 className="text-3xl md:text-4xl font-bold text-white mb-3">
                  Entre no <span className="text-[#E53935]">BBQ Jobs</span>
                </h1>
                <p className="text-gray-400 text-lg">
                  Escolha o tipo de perfil para comecar
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {/* Card Trabalhador */}
                <button
                onClick={() => {setProfileType('trabalhador');setStep('form');}}
                className="group relative bg-[#2a2a2a] border-2 border-[#3a3a3a] rounded-2xl p-8 text-center hover:border-[#E53935] transition-all duration-300 hover:shadow-xl hover:shadow-[#E53935]/10 hover:-translate-y-1">

                  <div className="w-20 h-20 mx-auto mb-5 rounded-full bg-gradient-to-br from-[#E53935]/20 to-[#C62828]/20 flex items-center justify-center group-hover:from-[#E53935]/30 group-hover:to-[#C62828]/30 transition-all">
                    <User size={36} className="text-[#E53935]" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2">Profissional</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">
                    Quero criar meu perfil e encontrar oportunidades na gastronomia brasileira.
                  </p>
                  <div className="mt-4 flex items-center justify-center gap-1 text-[#E53935] text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                    Comecar <ArrowRight size={16} />
                  </div>
                </button>

                {/* Card Restaurante */}
                <button
                onClick={() => {setProfileType('restaurante');setStep('form');}}
                className="group relative bg-[#2a2a2a] border-2 border-[#3a3a3a] rounded-2xl p-8 text-center hover:border-[#8D6E63] transition-all duration-300 hover:shadow-xl hover:shadow-[#8D6E63]/10 hover:-translate-y-1">

                  <div className="w-20 h-20 mx-auto mb-5 rounded-full bg-gradient-to-br from-[#8D6E63]/20 to-[#5D4037]/20 flex items-center justify-center group-hover:from-[#8D6E63]/30 group-hover:to-[#5D4037]/30 transition-all">
                    <UtensilsCrossed size={36} className="text-[#8D6E63]" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2">Estabelecimento</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">
                    Quero encontrar profissionais qualificados para meu estabelecimento
                  </p>
                  <div className="mt-4 flex items-center justify-center gap-1 text-[#8D6E63] text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                    Comecar <ArrowRight size={16} />
                  </div>
                </button>
              </div>

              {/* Ja tem conta */}
              <div className="text-center mt-8">
                <p className="text-gray-500">
                  Ja possui uma conta?{' '}
                  <button
                  onClick={() => {setStep('form');switchMode('signin');}}
                  className="text-[#E53935] font-semibold hover:underline">

                    Entrar
                  </button>
                </p>
              </div>
            </div>
          }

          {/* Etapa 2: Formulario de autenticacao */}
          {step === 'form' &&
          <div className="animate-fade-in">
              {/* Voltar */}
              <button
              onClick={resetToTypeSelection}
              className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors mb-6">

                <ArrowLeft size={18} />
                <span className="text-sm">Voltar</span>
              </button>

              {/* Header */}
              <div className="text-center mb-8">
                {profileType && mode === 'signup' &&
              <div className="inline-flex items-center gap-2 bg-[#2a2a2a] px-4 py-2 rounded-full mb-4 border border-[#3a3a3a]">
                    {profileType === 'trabalhador' ?
                <User size={16} className="text-[#E53935]" /> :

                <UtensilsCrossed size={16} className="text-[#8D6E63]" />
                }
                    <span className="text-sm text-gray-300 font-medium">
                      {profileType === 'trabalhador' ? 'Perfil Profissional' : 'Perfil Estabelecimento'}
                    </span>
                  </div>
              }
                <h2 className="text-2xl md:text-3xl font-bold text-white mb-2">
                  {mode === 'signin' ? 'Bem-vindo de volta' : mode === 'signup' ? 'Criar sua conta' : 'Redefinir senha'}
                </h2>
                <p className="text-gray-400">
                  {mode === 'signin' ?
                'Entre com suas credenciais para acessar sua conta' :
                mode === 'signup' ?
                'Preencha os dados abaixo para se cadastrar' :
                'Insira seu e-mail para receber o codigo de redefinicao'}
                </p>
              </div>

              {/* Card do formulario */}
              <div className="bg-[#2a2a2a] rounded-2xl p-8 border border-[#3a3a3a] shadow-2xl">
                {/* Botao Google */}
                {mode !== 'reset' &&
              <>
                    <button
                  onClick={handleGoogleLogin}
                  disabled={loading || needsCompletion}
                  className="w-full flex items-center justify-center gap-3 bg-white text-gray-800 font-semibold py-3.5 rounded-xl hover:bg-gray-100 transition-all shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed">

                      <svg width="20" height="20" viewBox="0 0 24 24">
                        <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4" />
                        <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                        <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                        <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
                      </svg>
                      {loading ? 'Conectando...' : 'Continuar com Google'}
                    </button>

                    <div className="flex items-center my-6">
                      <div className="flex-1 h-px bg-[#3a3a3a]"></div>
                      <span className="px-4 text-gray-500 text-sm">ou</span>
                      <div className="flex-1 h-px bg-[#3a3a3a]"></div>
                    </div>
                  </>
              }

                <form onSubmit={submit} className="space-y-5">
                  {/* Nome (apenas no cadastro) */}
                  {mode === 'signup' &&
                <div>
                      <label className="block text-gray-300 text-sm font-medium mb-2">
                        {profileType === 'restaurante' ? 'Nome do Estabelecimento' : 'Nome Completo'}
                      </label>
                      <div className="relative">
                        <div className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500">
                          {profileType === 'restaurante' ? <ChefHat size={18} /> : <User size={18} />}
                        </div>
                        <input
                      type="text"
                      value={userName}
                      onChange={(e) => setUserName(e.target.value)}
                      placeholder={profileType === 'restaurante' ? 'Ex: Churrascaria Fogo de Chao' : 'Ex: Joao Silva'}
                      className="w-full bg-[#1a1a1a] border border-[#3a3a3a] rounded-xl pl-12 pr-4 py-3.5 text-white placeholder-gray-600 focus:border-[#E53935] focus:ring-1 focus:ring-[#E53935] outline-none transition-all" />

                      </div>
                    </div>
                }

                  {/* Email */}
                  <div>
                    <label className="block text-gray-300 text-sm font-medium mb-2">E-mail</label>
                    <div className="relative">
                      <div className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500">
                        <Mail size={18} />
                      </div>
                      <input
                      type="email"
                      value={email}
                      disabled={loading || sendingOtp}
                      onChange={(e) => { setEmail(e.target.value); setOtp(''); setOtpSent(false); }}
                      placeholder="seu@email.com"
                      required
                      className="w-full bg-[#1a1a1a] border border-[#3a3a3a] rounded-xl pl-12 pr-4 py-3.5 text-white placeholder-gray-600 focus:border-[#E53935] focus:ring-1 focus:ring-[#E53935] outline-none transition-all" />

                    </div>
                  </div>

                  {/* OTP (cadastro e redefinicao) */}
                  {(mode === 'signup' || mode === 'reset') &&
                <div>
                      <label className="block text-gray-300 text-sm font-medium mb-2">
                        Codigo de Verificacao
                      </label>
                      <div className="flex gap-3">
                        <div className="relative flex-1">
                          <div className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500">
                            <CheckCircle2 size={18} />
                          </div>
                          <input
                        type="text"
                        value={otp}
                        onChange={(e) => setOtp(e.target.value)}
                        placeholder="Insira o codigo"
                        className="w-full bg-[#1a1a1a] border border-[#3a3a3a] rounded-xl pl-12 pr-4 py-3.5 text-white placeholder-gray-600 focus:border-[#E53935] focus:ring-1 focus:ring-[#E53935] outline-none transition-all" />

                        </div>
                        <button
                      type="button"
                      onClick={sendOtp}
                      disabled={countdown > 0 || sendingOtp || loading}
                      className="px-5 py-3.5 bg-gradient-to-r from-[#5D4037] to-[#4E342E] text-white rounded-xl font-medium hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap border border-[#8D6E63] text-sm">

                          {countdown > 0 ? `${countdown}s` : otpSent ? 'Reenviar' : 'Enviar'}
                        </button>
                      </div>
                      {otpSent &&
                  <p className="text-green-400 text-xs mt-2 flex items-center gap-1">
                          <CheckCircle2 size={12} />
                          Codigo enviado! Verifique sua caixa de entrada.
                        </p>
                  }
                    </div>
                }

                  {/* Senha */}
                  <div>
                    <label className="block text-gray-300 text-sm font-medium mb-2">
                      {mode === 'reset' ? 'Nova Senha' : 'Senha'}
                    </label>
                    <div className="relative">
                      <div className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500">
                        <Lock size={18} />
                      </div>
                      <input
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder={mode === 'reset' ? 'Sua nova senha' : 'Sua senha'}
                      required
                      className="w-full bg-[#1a1a1a] border border-[#3a3a3a] rounded-xl pl-12 pr-12 py-3.5 text-white placeholder-gray-600 focus:border-[#E53935] focus:ring-1 focus:ring-[#E53935] outline-none transition-all" />

                      <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300 transition-colors">

                        {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                  </div>

                  {/* Confirmar Senha (apenas no cadastro) */}
                  {mode === 'signup' &&
                <div>
                      <label className="block text-gray-300 text-sm font-medium mb-2">
                        Confirmar Senha
                      </label>
                      <div className="relative">
                        <div className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500">
                          <Lock size={18} />
                        </div>
                        <input
                      type={showPassword ? 'text' : 'password'}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Repita sua senha"
                      required
                      className="w-full bg-[#1a1a1a] border border-[#3a3a3a] rounded-xl pl-12 pr-4 py-3.5 text-white placeholder-gray-600 focus:border-[#E53935] focus:ring-1 focus:ring-[#E53935] outline-none transition-all" />

                      </div>
                    </div>
                }

                  {/* Termos de Uso e Politica de Privacidade (apenas no cadastro) */}
                  {mode === 'signup' &&
                <label className="flex items-start gap-3 cursor-pointer">
                      <input
                    type="checkbox"
                    checked={termsAccepted}
                    onChange={(e) => setTermsAccepted(e.target.checked)}
                    className="mt-1 w-4 h-4 rounded border-[#3a3a3a] bg-[#1a1a1a] text-[#E53935] focus:ring-[#E53935] shrink-0" />

                      <span className="text-gray-400 text-sm leading-relaxed">
                        Li e aceito os <strong className="text-gray-300">Termos de Uso</strong> e a <strong className="text-gray-300">Politica de Privacidade</strong>.
                      </span>
                    </label>
                }

                  {/* Erro */}
                  {error &&
                <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-3 text-red-400 text-sm">
                      {error}
                    </div>
                }

                  {/* Botao submit */}
                  <button
                  type="submit"
                  disabled={loading || needsCompletion}
                  className="w-full py-4 bg-gradient-to-r from-[#C62828] to-[#E53935] text-white font-bold rounded-xl hover:shadow-xl hover:shadow-[#E53935]/30 transition-all disabled:opacity-50 disabled:cursor-not-allowed text-lg border border-[#8D0000]">

                    {loading ?
                  'Processando...' :
                  mode === 'signin' ?
                  'Entrar' :
                  mode === 'signup' ?
                  'Criar Conta' :
                  'Redefinir Senha'}
                  </button>
                </form>

                {/* Links de alternancia */}
                <div className="mt-6 text-center space-y-3">
                  {mode === 'signin' &&
                <>
                      <button
                    onClick={() => switchMode('reset')}
                    className="text-gray-400 text-sm hover:text-[#E53935] transition-colors block mx-auto">

                        Esqueceu sua senha?
                      </button>
                      <p className="text-gray-500 text-sm">
                        Nao tem conta?{' '}
                        <button
                      onClick={() => {switchMode('signup');setStep('select-type');}}
                      className="text-[#E53935] font-semibold hover:underline">

                          Cadastre-se
                        </button>
                      </p>
                    </>
                }
                  {mode === 'signup' &&
                <p className="text-gray-500 text-sm">
                      Ja tem uma conta?{' '}
                      <button
                    onClick={() => switchMode('signin')}
                    className="text-[#E53935] font-semibold hover:underline">

                        Entrar
                      </button>
                    </p>
                }
                  {mode === 'reset' &&
                <p className="text-gray-500 text-sm">
                      Lembrou a senha?{' '}
                      <button
                    onClick={() => switchMode('signin')}
                    className="text-[#E53935] font-semibold hover:underline">

                        Voltar ao login
                      </button>
                    </p>
                }
                </div>
              </div>
            </div>
          }
        </div>
      </div>

      <Footer />
    </div>);

}

export default Cadastro;